import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from 'src/app/features/auth/services/auth.service';
import { LocalStorageService } from 'src/app/shared/localstorage.service';

/**
 * Single owner of "the session is over".
 *
 * Sessions are server-side now: clearing localStorage alone leaves the refresh
 * token live, so a copy could keep minting access tokens after Log out. One
 * place to do it keeps the logout buttons and the interceptor in step.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  /**
   * User-initiated sign-out: revoke server-side, then drop local state.
   *
   * Fire-and-forget - the token travels in the body, so the call survives the
   * local clear, and a network failure must not strand the user signed in.
   */
  logout(): void {
    const refreshToken = LocalStorageService.get('currentUser')?.refresh_token;

    if (refreshToken) {
      this.authService.logout(refreshToken).subscribe({ error: () => undefined });
    }

    this.clearLocalSession();
  }

  /**
   * Drop local session state and return to the login page.
   *
   * For paths where the server side is already settled (a rejected refresh);
   * calling logout there would revoke an already-dead token.
   */
  clearLocalSession(): Promise<boolean> {
    localStorage.removeItem('currentUser');
    return this.router.navigate(['']);
  }
}
